import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { atom, useAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { isEmpty } from 'lodash';
import { useSnackbar } from 'notistack';
import { useEffect } from 'react';

import PERMISSIONS from '@/constants/auth.constants';
import {
  DEFAULT_PAGE_SIZE,
  PONDURANCE_LABEL,
  PONDURANCE_NAME,
  RULES_FILTERS_STORAGE_KEY
} from '@/constants/common';
import type { SearchFilters } from '@/features/SearchBar';
import useHandleError from '@/hooks/useHandleError';
import { selectedOrganizationAtom } from '@/pages/Organizations';
import { axiosClient } from '@/services/BaseApi.service';
import { userAtom } from '@/store/user.store';
import type { Order } from '@/types/common';

import type {
  AllSensorRuleFile,
  SensorRuleFileDTO,
  SensorRuleFileKeys,
  UploadRuleParams
} from './AdministrationRules.types';

const jotaiSessionStorage = createJSONStorage<SearchFilters>(
  () => sessionStorage
);
export const rulesAtom = atom<AllSensorRuleFile[]>([]);
export const rulesCreateDialogOpenAtom = atom(false);
export const rulesDetailsAtom = atom<AllSensorRuleFile | null>(null);
export const rulesDetailsExpandedAtom = atom(false);
export const rulesDeleteDialogAtom = atom<SensorRuleFileDTO | null>(null);
export const rulesDeleteDialogOpenAtom = atom(false);
export const rulesTablePageAtom = atom(0);
export const rulesTableRowsAtom = atomWithStorage(
  'RulesTablesRows',
  DEFAULT_PAGE_SIZE
);
export const rulesTableOrderAtom = atomWithStorage<Order>(
  'RulesTableOrder',
  'asc'
);
export const rulesTableOrderByAtom = atomWithStorage<string>(
  'RulesTableOrderBy',
  'id'
);
export const rulesFiltersAtom = atomWithStorage(
  RULES_FILTERS_STORAGE_KEY,
  {},
  jotaiSessionStorage
);

export const filteredRulesAtom = atom((get) => {
  const rules = get(rulesAtom);
  const filters = get(rulesFiltersAtom);

  if (isEmpty(filters)) return rules;

  return rules.filter((rule) =>
    Object.keys(filters).every((k) =>
      filters[k].some((f) => {
        if (k === 'file_type') {
          return rule.file_type === f;
        }

        if (k === 'level') {
          return rule.level === f;
        }

        if (k === 'any') {
          return JSON.stringify(Object.values(rule))
            .toLocaleLowerCase()
            .includes(f.toLocaleLowerCase());
        }

        return rule[k as SensorRuleFileKeys] === f;
      })
    )
  );
});

export const useFetchSensorRules = () => {
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [rules, setRules] = useAtom(rulesAtom);
  const [userData] = useAtom(userAtom);
  const handleError = useHandleError();

  const { data, error, isError, isFetching, isSuccess } = useQuery({
    queryKey:
      selectedOrganization.label === 'all'
        ? ['sensor_rules/']
        : [`organization_settings/${selectedOrganization.label}/sensor_rules`],
    enabled: userData?.permissions.all.includes(PERMISSIONS.SENSOR_RULES_READ)
  });

  useEffect(() => {
    if (isError) {
      handleError(
        error as AxiosError,
        selectedOrganization.label === 'all'
          ? 'Failure to fetch all organization sensor rules.'
          : 'Failure to fetch organization sensor rules.'
      );
    }
  }, [isError]);

  useEffect(() => {
    // all organization sensor rules
    if (isSuccess && selectedOrganization.label === 'all') {
      const newArray: AllSensorRuleFile[] = [];
      (
        data as Array<{
          name: string;
          label: string;
          sensor_rules: SensorRuleFileDTO;
        }>
      ).forEach(
        ({
          name,
          label,
          sensor_rules
        }: {
          name: string;
          label: string;
          sensor_rules: SensorRuleFileDTO;
        }) => {
          if (Array.isArray(sensor_rules)) {
            sensor_rules.forEach((rule) => {
              newArray.push({
                ...rule,
                organization: name,
                organization_label: label,
                updated_at: rule?.logs?.[0]?.created_at
              });
            });
          }
        }
      );
      setRules(newArray);
      return;
    }

    // organization sensor rules
    if (isSuccess && selectedOrganization.label !== 'all') {
      const newArray = (data as SensorRuleFileDTO[]).map((item) => ({
        ...item,
        organization:
          item.level === 'global' ? PONDURANCE_NAME : selectedOrganization.name,
        organization_label:
          item.level === 'global'
            ? PONDURANCE_LABEL
            : selectedOrganization.label,
        updated_at: item?.logs?.[0]?.created_at
      }));
      setRules(newArray as AllSensorRuleFile[]);
    }
  }, [data, isSuccess, selectedOrganization]);

  return {
    isFetching,
    rules
  };
};

export const useDeleteSensorRule = () => {
  const [rulesDeleteDialog] = useAtom(rulesDeleteDialogAtom);
  const [, setIsRulesDeleteDialogOpen] = useAtom(rulesDeleteDialogOpenAtom);
  const [rulesDetails, setRulesDetails] = useAtom(rulesDetailsAtom);
  const [, setAreRulesDetailsExpanded] = useAtom(rulesDetailsExpandedAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (row: AllSensorRuleFile) => {
      return axiosClient.delete(
        `/organization_settings/${row.organization_label}/sensor_rules/${row.id}`
      );
    },
    onError: (error: AxiosError) => {
      handleError(error, 'Failure to delete sensor file.');
    },
    onSuccess: () => {
      enqueueSnackbar('Sensor File Deleted', {
        variant: 'success'
      });

      setIsRulesDeleteDialogOpen(false);
      if (rulesDetails?.id === rulesDeleteDialog?.id) {
        setAreRulesDetailsExpanded(false);
        setRulesDetails(null);
      }

      // refetch data
      queryClient.invalidateQueries({
        queryKey: ['sensor_rules/']
      });
      queryClient.invalidateQueries({
        queryKey: [
          `organization_settings/${selectedOrganization.label}/sensor_rules`
        ]
      });
    }
  });
};

export const useUploadSensorRule = (handleSuccess: () => void) => {
  const [rulesDetails, setRulesDetails] = useAtom(rulesDetailsAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const handleError = useHandleError();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const throwValidationError = (message: string) => {
    const error = new AxiosError();
    error.response = {
      status: 400,
      statusText: 'Bad Request',
      headers: {},
      config: {} as InternalAxiosRequestConfig,
      data: {
        error: 'VALIDATION_ERROR',
        message
      }
    };
    throw error;
  };

  return useMutation({
    mutationFn: ({
      file,
      file_type,
      key,
      sensor_rule_id
    }: UploadRuleParams) => {
      const formData = new FormData();
      const label =
        selectedOrganization.label === 'all'
          ? PONDURANCE_LABEL
          : selectedOrganization.label;

      formData.append('file', file);
      formData.append('file_type', file_type);
      formData.append(
        'level',
        selectedOrganization.label === 'all' ? 'global' : 'organization'
      );

      if ((key && !sensor_rule_id) || (!key && sensor_rule_id)) {
        throwValidationError('One or more update parameters are missing.');
      }

      if (key && sensor_rule_id) {
        formData.append('key', key);
        formData.append('sensor_rule_id', sensor_rule_id);

        return axiosClient.put(
          `/organization_settings/${label}/sensor_rules`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );
      }

      return axiosClient.post(
        `/organization_settings/${label}/sensor_rules`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );
    },

    onError: (error: AxiosError) => {
      handleError(error, 'Failure to upload sensor file.');
    },

    onSuccess: (response) => {
      enqueueSnackbar('Sensor File Uploaded', {
        variant: 'success'
      });

      handleSuccess();
      if (response.data?.key === rulesDetails?.key) {
        setRulesDetails(
          (prev: AllSensorRuleFile | null) =>
            prev && {
              ...prev,
              ...response.data
            }
        );
      }

      // refetch data
      queryClient.invalidateQueries({
        queryKey: ['sensor_rules/']
      });
      queryClient.invalidateQueries({
        queryKey: [
          `organization_settings/${selectedOrganization.label}/sensor_rules`
        ]
      });
    }
  });
};
